import type { PropTypes } from './DisconnectAllWalletsModal.types';
import type { ReactElement } from 'react';

import { AlertDialog, Button, Flex } from '@rango-dev/whale';
import { AlertSheet } from '@rango-dev/whale/lab';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useWindowDimensions } from '../../../hooks/useWindowDimensions';

const DIALOG_MAX_WIDTH = '600px';

/**
 * Destructive confirmation: a centered Radix AlertDialog on desktop, the
 * non-dismissible `AlertSheet` on mobile. Either way the user has to pick
 * Cancel or Disconnect.
 */
export function DisconnectAllWalletsModal(props: PropTypes): ReactElement {
  const { open, onClose, onConfirm } = props;
  const { isMobile } = useWindowDimensions();
  const { t } = useTranslation();

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onClose();
    }
  };

  const title = t('Disconnect all wallets?');
  const description = t(
    'This will disconnect all connected wallets and log you out of your profile.',
  );

  if (isMobile) {
    return (
      <AlertSheet
        open={open}
        onOpenChange={handleOpenChange}
        title={title}
        description={description}
        footer={
          <Flex
            direction={{ initial: 'column-reverse', sm: 'row' }}
            gap={{ initial: '2', sm: '3' }}
            height="100%">
            <Button
              id="disconnect-all-wallets-modal-no-btn"
              size="3"
              radius="full"
              variant="ghost"
              color="gray"
              onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              id="disconnect-all-wallets-modal-yes-btn"
              size="3"
              color="red"
              radius="full"
              onClick={onConfirm}>
              {t('Disconnect')}
            </Button>
          </Flex>
        }></AlertSheet>
    );
  }

  return (
    <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
      <AlertDialog.Content maxWidth={DIALOG_MAX_WIDTH}>
        <Flex direction="column" gap="4">
          <Flex direction="column" gap="3">
            <AlertDialog.Title mb="0" size="5">
              {title}
            </AlertDialog.Title>
            <AlertDialog.Description mb="0" size="2">
              {description}
            </AlertDialog.Description>
          </Flex>

          <Flex align="center" justify="end" gap="3">
            <AlertDialog.Cancel>
              <Button
                id="disconnect-all-wallets-modal-no-btn"
                size="3"
                radius="full"
                variant="ghost"
                color="gray">
                {t('Cancel')}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                id="disconnect-all-wallets-modal-yes-btn"
                size="3"
                color="red"
                radius="full"
                onClick={onConfirm}>
                {t('Disconnect')}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}
